import { useEffect, useMemo, useState } from 'react';
import { throttle } from 'lodash';
import { cloneDeep } from 'lodash/lang';
import { omit, set } from 'lodash/object';
import { normalizeCardId, normalizePostalCode, normalizeSin, normalizeSsn } from 'services/normalizers';
import { parseYupRequiredFields } from 'services/yup';

import {
  addValidationSchema,
  medicalRecordNumberValidationSchema
} from 'components/pages/patient-source/Patients/PatientInfo/PatientInfoMainSection/AddOrEditPatientInfoModal/services/validationService/validationSchema';

import useAppInfo from '../../../../../../../common/hooks/useAppInfo';
import { setIn } from '../../../../../../../services/immutable';
import { PAYMENT_TYPE_DIGITAL, PAYMENT_TYPE_PHYSICAL } from '../../../constants';

import { parsePatientInfo } from './AddOrEditPatientInfoModalService';

export function useInfo(patientInfo) {
  const appInfo = useAppInfo();
  const [validationMessages, setValidationMessages] = useState({});
  const notUsInstance = appInfo.features?.notUsInstance;
  const defaultCountryId = notUsInstance ? '' : 'US';
  const defaultPhoneCountryId = notUsInstance ? null : 'US';

  const parsedPatientInfo = useMemo(
    function() {
      if (!patientInfo) {
        return {
          firstName: '',
          lastName: '',
          middleName: '',
          preferredName: '',
          dob: '',
          phonesInfo: {
            alternative: {
              countryId: defaultPhoneCountryId,
              nationalNumber: '',
              phoneType: null
            },
            primary: {
              countryId: defaultPhoneCountryId,
              nationalNumber: '',
              phoneType: null
            },
            preferredContactMethod: '',
            neverCallAgain: false,
            voicemailNotAllowed: false
          },
          email: '',
          address: { country: defaultCountryId, addressLine1: '', addressLine2: '', city: '', state: '', zipCode: '' },
          ssn: '',
          cardId: '',
          guardianFirstName: '',
          guardianLastName: '',
          guardianEmail: '',
          guardianPhone: {
            countryId: defaultPhoneCountryId,
            nationalNumber: '',
            phoneType: null
          },
          guardianDob: '',
          sex: '',
          pronouns: '',
          isDeceased: false,
          primaryLanguage: '',
          smsOptIn: 'NOT_ASKED',
          interestedInFutureResearch: 'NOT_ASKED',
          heightFt: '',
          heightIn: '',
          weight: '',
          races: [],
          ethnicities: [],
          digitalPaymentAllowed: false,
          ssnOptional: false,
          paymentType: null
        };
      }
      return parsePatientInfo(patientInfo, defaultPhoneCountryId);
    },
    [patientInfo, defaultPhoneCountryId, defaultCountryId]
  );

  const [info, setInfo] = useState(parsedPatientInfo);

  const isEditMode = useMemo(
    function() {
      return !!patientInfo?.id;
    },
    [patientInfo]
  );

  function handleCountryChange(state) {
    if (state?.id === info.address?.country) {
      return;
    }
    resetValidationMessageByName('address.country');
    setInfo(function(info) {
      return {
        ...info,
        ssn: '',
        cardId: '',
        paymentType: null,
        address: {
          ...info.address,
          country: state?.id,
          state: '',
          zipCode: ''
        }
      };
    });
  }

  function handleStateChange(state, name) {
    resetValidationMessageByName(name);

    setInfo(function(info) {
      return {
        ...info,
        address: {
          ...info.address,
          state: state?.id
        }
      };
    });
  }

  function handleSexChange(sex) {
    setInfo(function(info) {
      return {
        ...info,
        sex: sex?.id
      };
    });
  }

  function handlePronounsChange(pronouns) {
    setInfo(function(info) {
      return {
        ...info,
        pronouns: pronouns?.id
      };
    });
  }

  function handlePrimaryLanguageChange(language) {
    setInfo(function(info) {
      return {
        ...info,
        primaryLanguage: language?.id
      };
    });
  }

  function handlePreferredContactMethodChange(contactMethod) {
    setInfo(function(info) {
      return {
        ...info,
        phonesInfo: {
          ...info.phonesInfo,
          preferredContactMethod: contactMethod?.id
        }
      };
    });
  }

  function handleHeightChange(height, heightKey) {
    setInfo(function(info) {
      return {
        ...info,
        [heightKey]: height?.value
      };
    });
  }

  function handleRacesChange(races) {
    setInfo(function(info) {
      return {
        ...info,
        races: [...races].map(race => race.id)
      };
    });
  }

  function handleEthnicitiesChange(ethnicities) {
    setInfo(function(info) {
      return {
        ...info,
        ethnicities: [...ethnicities].map(ethnicity => ethnicity.id)
      };
    });
  }

  function handleDateOfBirthChange(dob, name) {
    resetValidationMessageByName(name);

    setInfo(function(info) {
      return {
        ...info,
        dob
      };
    });
  }

  function handleGuardianDobChange(guardianDob, name) {
    resetValidationMessageByName(name);

    setInfo(function(info) {
      return {
        ...info,
        guardianDob
      };
    });
  }

  function handlePhoneNumberChange(target) {
    resetValidationMessageByName(target.name);

    setInfo(function(info) {
      return setIn(info, target.name, omit(target, 'name'), true);
    });
  }

  function handleInputChange({ target: { type, checked, value: inputValue, name } }) {
    const value = type === 'checkbox' ? checked : inputValue;

    resetValidationMessageByName(name);

    setInfo(function(info) {
      const cloned = cloneDeep(info);
      const isUS = info.address.country === 'US';

      if (name === 'cardId') {
        return set(cloned, name, normalizeCardId(value));
      }

      if (name === 'ssn') {
        return set(cloned, name, isUS ? normalizeSsn(value) : normalizeSin(value));
      }

      if (name === 'address.zipCode') {
        return set(cloned, name, normalizePostalCode(value, info.address.country));
      }

      return set(cloned, name, value);
    });
  }

  function resetValidationMessageByName(name) {
    if (validationMessages[name]) {
      setValidationMessages(function(state) {
        return omit(state, name);
      });
    }
  }
  function handleChangeSmsOptInDropdown(option) {
    if (option && option.id !== info.smsOptIn) {
      setInfo(function(info) {
        return {
          ...info,
          smsOptIn: option.id
        };
      });
    }
  }

  function handlePaymentTypeChange(value) {
    resetValidationMessageByName('cardId');
    setInfo(function(info) {
      return !value || value === PAYMENT_TYPE_DIGITAL
        ? {
            ...info,
            paymentType: value,
            cardId: null
          }
        : {
            ...info,
            paymentType: value
          };
    });
  }

  function handleChangeInterestedInFutureResearch(option) {
    if (option && option.id !== info.interestedInFutureResearch) {
      setInfo(function(info) {
        return {
          ...info,
          interestedInFutureResearch: option.id
        };
      });
    }
  }

  function handleChangeDigitalPaymentAllowed(digitalPaymentAllowed) {
    setInfo(function(info) {
      const nextState = {
        ...info,
        digitalPaymentAllowed
      };
      if (digitalPaymentAllowed && info?.paymentType !== PAYMENT_TYPE_PHYSICAL) {
        nextState.cardId = '';
      }
      if (!digitalPaymentAllowed) {
        nextState.paymentType = null;
      }
      return nextState;
    });
  }

  function handleChangeOptionalSsnAllowed(ssnOptional) {
    setInfo(info => ({ ...info, ssnOptional }));
  }

  return {
    info,
    setInfo,
    isEditMode,
    validationMessages,
    setValidationMessages,
    handleCountryChange,
    handleStateChange,
    handleDateOfBirthChange,
    handleGuardianDobChange,
    handlePhoneNumberChange,
    handleInputChange,
    handleSexChange,
    handlePronounsChange,
    handlePrimaryLanguageChange,
    handlePreferredContactMethodChange,
    handleHeightChange,
    handleRacesChange,
    handleEthnicitiesChange,
    resetValidationMessageByName,
    handleChangeSmsOptInDropdown,
    handleChangeInterestedInFutureResearch,
    handlePaymentTypeChange,
    handleChangeDigitalPaymentAllowed,
    handleChangeOptionalSsnAllowed
  };
}

const throttleValidationDescribe = throttle(cb => cb(), 1000);

export function useRequiredFields(info, originalPID, patientStipendFieldsRequired) {
  const [requiredFields, setRequiredFields] = useState({});

  useEffect(
    function() {
      throttleValidationDescribe(function() {
        const { cardId, paymentType } = info;
        const financialFieldsRequired = cardId || paymentType || patientStipendFieldsRequired;
        const addSchema = addValidationSchema.describe({
          value: info,
          context: { originalPID, financialFieldsRequired }
        });
        const mrnSchema = medicalRecordNumberValidationSchema.describe({
          value: { medicalRecordNumber: null },
          context: {
            patientStipendFieldsRequired
          }
        });
        const addRequiredFields = parseYupRequiredFields(addSchema);
        const mrnRequiredFields = parseYupRequiredFields(mrnSchema);

        setRequiredFields({ ...addRequiredFields, ...mrnRequiredFields });
      });
    },
    [info, originalPID, patientStipendFieldsRequired]
  );

  function isRequiredField(field) {
    return !!requiredFields[field];
  }

  return [isRequiredField];
}
